import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { AccountBalance, LedgerAccount } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { amountValue, localDateTimeValue, money, parseAmount } from "../../shared/lib/format";
import { DateTimeField } from "../../shared/ui/DateTimeField";
import { reconcileAccount, type ReconciliationResult } from "./api";

interface ReconcilePanelProps {
  bookID: string;
  account: LedgerAccount;
  balance?: AccountBalance;
  onNotify: (message: string) => void;
  // Inside a disclosure the surrounding panel and heading are already there.
  bare?: boolean;
}

// Reconciliation records the difference between what the journal derives and
// what the bank shows as one auditable non-flow adjustment: never an expense,
// never income, never a change to history.
export function ReconcilePanel({ bookID, account, balance, onNotify, bare = false }: ReconcilePanelProps) {
  const queryClient = useQueryClient();
  const [declared, setDeclared] = useState("");
  const [effectiveAt, setEffectiveAt] = useState(localDateTimeValue());
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const mutation = useMutation({
    mutationFn: () => reconcileAccount(bookID, account.id, { declaredBalance: parseAmount(declared, true) ?? "", currency: account.currency, effectiveAt: new Date(effectiveAt).toISOString(),
      balanceKind: "account" }),
    onSuccess: async (value) => {
      setResult(value);
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["statement"] });
      onNotify(value.created
        ? `Adjusted ${account.name} by ${money(value.difference.minor, value.difference.currency, true)}.`
        : "Ledgerly already matches the declared balance.");
    },
  });
  const current = balance ? balance.nativeBalance.minor : 0;
  // A liability's journal figure is what is owed, so every label here says so.
  // The reported card balance is a different number and is recorded elsewhere.
  const owed = false;
  const declaredValid = parseAmount(declared, true) !== null;

  return (
    <section className={bare ? "bare-section" : "panel reconcile-panel"} aria-labelledby="reconcile-title">
      {bare ? null : <div className="panel-heading"><div><p className="eyebrow">Reconcile</p><h2 id="reconcile-title">Match the bank</h2></div></div>}
      <p className="panel-note">
        {owed
          ? "Enter the outstanding debt your bank states for this card at the chosen time — not the account balance it prints beside it. If it differs from the journal, Ledgerly records the difference as an adjustment, never as income or an expense."
          : "Enter the balance your bank or wallet shows for this account at the chosen time. If it differs from the journal, Ledgerly records the difference as an adjustment, not as income or an expense."}
      </p>
      <form className="reconcile-form" onSubmit={(event) => { event.preventDefault(); if (declaredValid) mutation.mutate(); }}>
        <label>{owed ? "Ledgerly debt" : "Ledgerly balance"}<output>{money(current, account.currency)}</output></label>
        <label>{owed ? "Declared debt" : "Declared balance"}<span className="amount-input"><input value={declared} onChange={(event) => setDeclared(event.target.value)} inputMode="decimal" placeholder={amountValue(current, account.currency)} required /><span>{account.currency}</span></span></label>
        <DateTimeField label="As of" value={effectiveAt} onChange={setEffectiveAt} required />
        <button className="primary-button compact" type="submit" disabled={!declaredValid || mutation.isPending}><span>{mutation.isPending ? "Reconciling…" : "Reconcile"}</span><span aria-hidden="true">→</span></button>
      </form>
      {mutation.error ? <p className="form-error" role="alert">{humanError(mutation.error)}</p> : null}
      {result ? (
        <p className="panel-note">Journal {money(result.calculatedBalance.minor, result.calculatedBalance.currency)}, declared {money(result.declaredBalance.minor, result.declaredBalance.currency)}, difference {money(result.difference.minor, result.difference.currency, true)}{result.adjustment ? " — adjustment recorded." : " — nothing to record."}</p>
      ) : null}
    </section>
  );
}
